import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Modal } from '../ui/Modal';

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);

/**
 * Create a brand-new organization. The current user becomes its owner and
 * the active session is switched to the new org server-side.
 */
export function CreateOrgModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [touchedSlug, setTouchedSlug] = useState(false);

  // Auto-derive the slug from the name until the user types into the slug field.
  const effectiveSlug = touchedSlug ? slug : slugify(name);

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string; slug: string; name: string }>('/api/orgs', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), slug: effectiveSlug || undefined }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries();
      onClose();
    },
  });

  const errorMsg = create.error instanceof Error ? create.error.message : null;

  return (
    <Modal
      onClose={onClose}
      width="sm"
      title="New organization"
      footer={
        <>
          <button className="btn btn-secondary flex-1 sm:flex-none" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary flex-1 sm:flex-none"
            onClick={() => create.mutate()}
            disabled={!name.trim() || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create organization'}
          </button>
        </>
      }
    >
      <p className="text-sm text-text3 mb-4">
        You'll be the owner. Stages, activity types, custom fields and close-reasons get seeded so
        you can start using it immediately.
      </p>
      <label className="block mb-3">
        <span className="text-xs text-text2 block mb-1">Name</span>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme Sales"
          autoCapitalize="words"
          enterKeyHint="next"
        />
      </label>
      <label className="block mb-1">
        <span className="text-xs text-text2 block mb-1">Slug (URL identifier)</span>
        <input
          value={effectiveSlug}
          onChange={(e) => {
            setTouchedSlug(true);
            setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
          }}
          placeholder="auto"
          autoCapitalize="none"
          autoCorrect="off"
          enterKeyHint="done"
        />
      </label>
      <p className="text-[11px] text-text3 mb-3">
        Lowercase letters, numbers, hyphens. We'll add a suffix if it collides.
      </p>
      {errorMsg && <div className="text-red text-sm mb-3">{errorMsg}</div>}
    </Modal>
  );
}
